import { PublicLink } from "../components/PublicLink";
import { useEffect, type ReactNode } from "react";
import { studies, studyForPath, type Study } from "./ResearchContent";

export const BASE = import.meta.env.BASE_URL;
export const REPOSITORY_URL = "https://github.com/apolmig/agencytransfer";
export const LAB_URL = "https://agency-transfer-lab.miguelguerrero.eu";
export const REGISTRY_DATA_URL = "https://huggingface.co/datasets/apol/agency-transfer-benchmark";
export const ELECTION_INDEX_URL = "https://huggingface.co/datasets/apol/ai-election-manipulation-cases";
export const POLICY_ATLAS_URL = "https://huggingface.co/datasets/apol/agency-transfer-policy-atlas";
export const MANUEL_VIDEO_URL = "https://www.youtube.com/watch?v=ZTyWOhF7Pto";
export const route = (path = "") => `${BASE}${path.replace(/^\/+/, "")}`;
// Absolute URL and a new tab: the Registry remains an independent tool.
export const REGISTRY_URL = new URL(route("registry/"), window.location.origin).href;
export const isRegistryLink = (href: string) => {
  try { const url = new URL(href, window.location.origin); return url.origin === window.location.origin && url.pathname.startsWith(route("registry/")); }
  catch { return false; }
};
export const PROGRAMME_SCOPE = "Research on AI, manipulation and democracy.";
export const RIFT_ANIMATION = route("explainers/");
export const RIFT_PLAYER = route("media/cde-rift-animation/player.html");
export const RIFT_IMAGE = route("media/cde-rift-hero.png");

export const getPath = () => {
  const base = BASE.replace(/\/$/, "");
  let path = window.location.pathname;
  if (base && (path === base || path.startsWith(`${base}/`))) path = path.slice(base.length);
  const clean = path.replace(/index\.html$/, "").replace(/^\/+|\/+$/g, "");
  return clean === "media/cde-rift-animation" ? "explainers" : clean;
};
const metadata: Record<string, { title: string; description: string }> = {
  "": { title: "Harmful Manipulation and Election Security", description: "How AI can be used to manipulate people, what we know about its effects, and what could reduce the harm." },
  research: { title: "Research · AI, manipulation and elections", description: "What we tested, what the sources show, and what we still do not know." },
  paper: { title: "Research summary · AI, manipulation and elections", description: "The argument and early findings. The full working paper is not yet published." },
  references: { title: "Sources · AI, manipulation and elections", description: "Find the studies, reports and records behind the research." },
  outputs: { title: "Resources · AI, manipulation and elections", description: "Read the research and open its sources, data and tools." },
  explainers: { title: "Understanding the gap · AI, manipulation and elections", description: "What AI can do is not the same as what happens when people use it." },
  about: { title: "About the research · Miguel Guerrero", description: "The project, its author and how to read its findings." },
};
export function useMetadata(path: string) {
  useEffect(() => {
    const study = studyForPath(path);
    const entry = study ? { title: study.title, description: study.question } : metadata[path] ?? metadata[""];
    const title = `${entry.title} · Working draft`;
    const description = `${entry.description} Research in progress, not yet peer reviewed.`;
    const url = `https://miguelguerrero.eu/agencytransfer/${path ? `${path}/` : ""}`;
    document.title = title;
    for (const [selector, value] of [
      ['meta[name="description"]', description], ['meta[property="og:title"]', title],
      ['meta[property="og:description"]', description], ['meta[property="og:url"]', url],
    ]) { const meta = document.querySelector<HTMLMetaElement>(selector); if (meta) meta.content = value; }
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) canonical.href = url;
    document.body.classList.add("editorial-v2-active");
    return () => document.body.classList.remove("editorial-v2-active");
  }, [path]);
}
const activeSection = (path: string) => !path ? "overview" : path === "research" || path.startsWith("research/") ? "research" : ["outputs", "references", "explainers", "paper"].includes(path) ? "resources" : "about";

export function RegistryLink({ children = "Registry", className = "plain-registry" }: { children?: ReactNode; className?: string }) {
  return <PublicLink className={className} href={REGISTRY_URL} target="_blank" rel="noopener noreferrer">{children}<span aria-hidden="true"> ↗</span><span className="sr-only"> (opens a new tab)</span></PublicLink>;
}
export function SiteHeader({ path }: { path: string }) {
  const active = activeSection(path);
  return <header className="v2-header">
    <PublicLink className="v2-wordmark" href={route()} aria-label="Agency Transfer home"><strong>Agency Transfer</strong></PublicLink>
    <nav aria-label="Primary navigation">{[["overview", "Overview", ""], ["research", "Research", "research/"], ["resources", "Resources", "outputs/"], ["about", "About", "about/"]].map(([id, label, href]) => <PublicLink key={id} href={route(href)} aria-current={active === id ? "page" : undefined}>{label}</PublicLink>)}</nav>
    <RegistryLink />
  </header>;
}
export function DraftBar() {
  return <div className="v2-draftbar" aria-label="Publication status"><p>Research in progress. Not yet peer reviewed.</p></div>;
}
export function SiteFooter() {
  return <footer className="v2-footer">
    <p>Miguel Guerrero · ERA:AI Fellowship, Cambridge · 2026</p>
    <div className="v2-footer-links"><PublicLink href={route("references/")}>Sources</PublicLink><PublicLink href={route("about/#publication-policy")}>Publication policy</PublicLink><RegistryLink className="plain-footer-registry" /></div>
    <p className="plain-small">The tests, evaluation review and election cases cover evidence up to 28 August 2026. The policy review is dated 1 September 2026.</p>
  </footer>;
}
export function DraftBoundary({ children }: { children: ReactNode }) {
  return <aside className="plain-note"><p>{children}</p></aside>;
}
export function TextLink({ href, children, external = false }: { href: string; children: ReactNode; external?: boolean }) {
  const newTab = external || isRegistryLink(href);
  return <PublicLink className="v2-text-link" href={href} target={newTab ? "_blank" : undefined} rel={newTab ? "noopener noreferrer" : undefined}>{children}{newTab ? <><span aria-hidden="true"> ↗</span><span className="sr-only"> (opens a new tab)</span></> : null}</PublicLink>;
}
export function StudyNavigation({ current }: { current?: string }) {
  return <nav className="publication-study-nav" aria-label="Research studies">{studies.map(study => <PublicLink key={study.slug} href={route(`research/${study.slug}/`)} aria-current={study.slug === current ? "page" : undefined}>{study.name}</PublicLink>)}</nav>;
}
function StudySummary({ study }: { study: Study }) {
  return <section className="publication-study-summary" aria-label="What we did and found"><p>{study.method}</p><p>{study.finding} {study.limitation}</p></section>;
}
export function StudyMaterials({ slug }: { slug: string }) {
  const study = studies.find(item => item.slug === slug);
  if (!study) return null;
  return <section className="publication-study-sources"><h2>Available material</h2><p>{study.resources.map((item, index) => <span key={item.href}>{index ? " · " : ""}<TextLink href={/^https?:/.test(item.href) ? item.href : route(item.href)} external={/^https?:/.test(item.href)}>{item.label}</TextLink></span>)}</p><p className="plain-small">This page draws on records up to <time dateTime={study.cutoff}>{study.cutoffLabel}</time>.</p></section>;
}
export function PageLead({ eyebrow, title, deck, children }: { eyebrow: string; title: string; deck: string; children?: ReactNode }) {
  const study = studyForPath(getPath());
  return <>{study ? <StudyNavigation current={study.slug} /> : null}<section className="v2-page-lead"><p className="v2-eyebrow">{study?.name ?? eyebrow}</p><h1>{study?.title ?? title}</h1><p className="v2-deck">{study?.question ?? deck}</p>{!study ? children : null}</section>{study ? <StudySummary study={study} /> : null}</>;
}
